import React, { useState, useEffect, useRef } from 'react';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer.js';
import Map from '@arcgis/core/Map.js';
import MapView from '@arcgis/core/views/MapView.js';
import './HsCode.css';

const HsCode: React.FC = () => {
  interface AttributeMap {
    [key: string]: string; // This says every string key returns a string
  }

  const attributeMapping: AttributeMap = {
    w11_11: 'Product description',
    Is_it_possible_to_extract_enoug:
      'Is it possible to extract enough high quality DNA from this category of product for effective testing?',
    Any_qualifiers___CAVEAT_TEXT_: 'Any qualifiers?',
    Is_there_evidence_that_stable_i:
      'Is there evidence that stable isotope measurements taken from this category of forest products is consistent with stable isotope measurements taken from reference samples of unprocessed wood?',
    Any_qualifiers___isotope_measur: 'Any qualifiers? (stable isotope)',
    Is_there_evidence_that_trace_el:
      'Is there evidence that trace element measurements taken from this category of forest products is consistent with trace element measurements taken from reference samples of unprocessed wood?',
    Any_qualifiers___trace_element_: 'Any qualifiers? (trace element)',
    F1__Covered_under_the_scope_of_: 'Covered under the scope of the UKTR in 2023',
    F2__Lacey_Declaration_requireme: 'Lacey Declaration requirement as of 2023',
    F3__Covered_under_the_scope_of_: 'Covered under the scope of the Australian ILPA in 2023',
    F4__Covered_under_the_scope_of_: 'Covered under the scope of the EUDR (to become operational in 2024)',
    Genus___Species_qualifier_in_HS: 'Genus / Species qualifier in HS code',
  };

  const displayAttributes = [
    'w11_11',
    'Is_it_possible_to_extract_enoug',
    'Any_qualifiers___CAVEAT_TEXT_',
    'Is_there_evidence_that_stable_i',
    'Any_qualifiers___isotope_measur',
    'Is_there_evidence_that_trace_el',
    'Any_qualifiers___trace_element_',
    'F1__Covered_under_the_scope_of_',
    'F2__Lacey_Declaration_requireme',
    'F3__Covered_under_the_scope_of_',
    'F4__Covered_under_the_scope_of_',
    'Genus___Species_qualifier_in_HS',
  ];

  interface Attributes {
    [key: string]: any; // Replace `any` with a more specific type if possible
  }

  interface AttributeAccumulator {
    [key: string]: string | null; // or just 'any' if values can really be of any type
  }

  const questionsWithSeparators = [
    'Product description',
    'Any qualifiers?',
    'Any qualifiers? (stable isotope)',
    'Any qualifiers? (trace element)',
  ];
  const initialized = useRef(false);
  const hsCodeFeatureLayer = useRef<FeatureLayer | null>(null);
  const [hsCodes, setHsCodes] = useState<string[]>([]);
  const [selectedAttributes, setSelectedAttributes] = useState<Attributes>({});
  // const [description, setDescription] = useState('');
  const referenceDataFeatureLayer = useRef<FeatureLayer | null>(null);
  const [mapViewDivDetermination, setMapViewDivDetermination] = useState<any>(null); // Initialize mapViewRef here
  const [showMap, setShowMap] = useState(false);
  const [numSamplesForGenus, setNumSamplesForGenus] = useState(new Set());
  const [numCountriesForGenus, setNumCountriesForGenus] = useState(new Set());
  const [genusSpeciesName, setGenusSpeciesName] = useState('');
  // const { user, checkSignIn } = useAuth();

  const initializeReferenceDataLayer = async () => {
    try {
      referenceDataFeatureLayer.current = new FeatureLayer({
        url: 'https://services2.arcgis.com/HlCEjEqEP9GdOQeZ/arcgis/rest/services/analyzed_unanalyzed_combined_data_dev/FeatureServer',
      });
      await referenceDataFeatureLayer.current.load();
    } catch (error) {
      console.error('Reference data layer initialization failed: ', error);
    }
  };

  const initializeWindow = async () => {
    try {
      const map = new Map({
        basemap: 'topo-vector', // or any other basemap
      });

      const view = new MapView({
        container: 'mapViewDivDetermination', // Reference to the DOM element
        map: map,
        zoom: 2, // Starting zoom level
        center: [10.122105, 29.625366], // Center of the map (longitude, latitude)
      });

      // Store the view in a ref if you need to access it elsewhere
      setMapViewDivDetermination(view);

      hsCodeFeatureLayer.current = new FeatureLayer({
        url: 'https://services2.arcgis.com/HlCEjEqEP9GdOQeZ/arcgis/rest/services/20231207_HS_codes_testing_reference_data_integration___Timber/FeatureServer',
      });

      console.log(hsCodeFeatureLayer.current);

      // Query the countryBordersFeatureLayer for all countries, to extract the country names and display them in the dropdown
      const query = hsCodeFeatureLayer.current.createQuery();
      query.where = '1=1';
      query.outFields = ['*'];
      // Execute the query
      hsCodeFeatureLayer.current.queryFeatures(query).then(function (response) {
        // The 'response' contains the features of the layer
        const features = response.features;
        // check if the feature attrubute HS_code contains only numbers and . even an empty string is not allowed. Remove all white space from the string before checking
        // if not, remove the feature from the array
        features.forEach((feature, index) => {
          if (!/^[0-9.]+$/.test(feature.attributes.HS_code) || feature.attributes.HS_code.trim() === '') {
            features.splice(index, 1);
          }
        });
        setHsCodes(features.map((feature) => feature.attributes.HS_code));
        console.log(features);
      });
    } catch (error) {
      console.error('Window initialization failed: ', error);
    }
  };

  const handleHsCodeChange = async (evt: React.ChangeEvent<HTMLSelectElement>) => {
    console.log('HS Code selected: ', evt.target.value);

    if (hsCodeFeatureLayer.current && referenceDataFeatureLayer.current) {
      const query = hsCodeFeatureLayer.current.createQuery();
      query.where = `HS_code = '${evt.target.value}'`;
      const response = await hsCodeFeatureLayer.current.queryFeatures(query);
      const features = response.features;
      console.log(features);

      if (features.length > 0) {
        // Create an object with keys in the order defined by displayAttributes
        const orderedAttributes = displayAttributes.reduce<AttributeAccumulator>((acc, key) => {
          const attributeKey = Object.keys(attributeMapping).find((mappedKey) => mappedKey === key);
          if (attributeKey) {
            // Check if key exists in attributeMapping
            const fullAttributeName = attributeMapping[attributeKey] || attributeKey;
            acc[fullAttributeName] = features[0].attributes[attributeKey] || null; // Handle potential undefined values
          }
          // const attributeKey = Object.keys(attributeMapping).find(mappedKey => mappedKey === key);
          // if (attributeKey && features[0].attributes[attributeKey] !== undefined) {
          //   const fullAttributeName = attributeMapping[attributeKey] || attributeKey; // Use mapped name or key as fallback
          //   acc[fullAttributeName] = features[0].attributes[attributeKey];
          // }
          return acc;
        }, {});

        console.log('Ordered Attributes: ', orderedAttributes);
        setSelectedAttributes(orderedAttributes);
        // setDescription(features[0].attributes.w11_11);

        const genusSpeciesQualifier = features[0].attributes.Genus___Species_qualifier_in_HS;
        setGenusSpeciesName(genusSpeciesQualifier);
        if (
          genusSpeciesQualifier &&
          genusSpeciesQualifier !== 'Coniferous' &&
          genusSpeciesQualifier !== 'Non-coniferous'
        ) {
          console.log('GGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGenus / Species qualifier in HS code: ', genusSpeciesQualifier);
          console.log(
            '2222GGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGenus / Species qualifier in HS code: ',
            genusSpeciesQualifier.trim().split(', ')
          );

          //Perform filtering on referenceDataFeatureLayer
          const query = referenceDataFeatureLayer.current.createQuery();
          console.log(
            "Category = 'CAT3' AND Analyzed = 'analyzed' AND Genus IN ('" +
              [...genusSpeciesQualifier.trim().split(', ')].join("','") +
              "')"
          );
          referenceDataFeatureLayer.current.definitionExpression =
            "Category = 'CAT3' AND Analyzed = 'analyzed' AND Genus IN ('" +
            [...genusSpeciesQualifier.trim().split(', ')].join("','") +
            "')"; // Adjust the attribute name if necessary
          const response = await referenceDataFeatureLayer.current.queryFeatures(query);
          const referenceFeatures = response.features;

          const countries = new Set();
          const identifiers = new Set();
          referenceFeatures.forEach((feature) => {
            countries.add(feature.attributes.Country);
            identifiers.add(feature.attributes.Identifier);
          });
          setNumCountriesForGenus(countries);
          setNumSamplesForGenus(identifiers);

          console.log('Filtered Reference Data Features: ', referenceFeatures);
          // You can then use 'referenceFeatures' as needed in your component

          //empty the map
          mapViewDivDetermination.map.removeAll();
          // Assuming mapViewRef.current is your MapView instance

          // referenceDataFeatureLayer.current.definitionExpression = "Category = 'CAT3' AND Analyzed = 'analyzed' AND Genus = '"+genusSpeciesQualifier+"'"; // Adjust the attribute name if necessary

          mapViewDivDetermination.map.add(referenceDataFeatureLayer.current);
          setShowMap(true);
          console.log('setting showMap to ', showMap);
        } else {
          mapViewDivDetermination.map.removeAll();
          setShowMap(false);
          console.log('setting showMap to ', showMap);
        }
      } else {
        setSelectedAttributes({});
      }
    } else {
      console.error('Feature layer is not initialized');
    }
  };

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      // checkSignIn();

      initializeWindow();
      initializeReferenceDataLayer();
    }
  }, []);

  return (
    <div className='hsCode-container container mx-auto px-4'>
      <div className='hsCode-description p-4 mt-8 mb-4 bg-blue-100 rounded-lg border border-blue-200 text-blue-700 text-sm'>
        <span>
          This page can help you understand if testing is a viable option for the product that you are concerned about,
          and which techniques are possible. Where HS codes specify genus/species information the page can also give you
          an insight into what reference samples/data WFID has available, in order to answer your query.
        </span>
      </div>

      <div className='select-component flex flex-col items-center mb-4'>
        <label htmlFor='hs-code-select' className='select-label text-lg font-medium text-gray-700 my-2'>
          Select an HS-code
        </label>
        <select
          id='hs-code-select'
          className='w-full pl-3 pr-10 py-2 text-base bg-gray-200 border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md'
          onChange={handleHsCodeChange}
        >
          <option value=''>HS-codes</option>
          {hsCodes.map((hsCode, index) => (
            <option key={index} value={hsCode}>
              {hsCode} - {hsCode}
            </option>
          ))}
        </select>
      </div>

      <div className='space-y-4'>
        {Object.keys(selectedAttributes).map((key, index) => {
          const value = selectedAttributes[key];
          return (
            <React.Fragment key={index}>
              {value !== null && (
                <div className='flex flex-col p-2 bg-gray-50 rounded shadow-sm'>
                  <div className='flex justify-between'>
                    <strong>{key}:</strong>
                  </div>
                  <div className='flex mt-5'>
                    <strong>
                      <i>Answer:</i>
                    </strong>
                  </div>
                  <span>{value}</span>
                </div>
              )}

              {questionsWithSeparators.includes(key) && <hr className='border-t border-gray-200' />}
            </React.Fragment>
          );
        })}
        {showMap && (
          <div className='hscode-map-container p-2 bg-green-100 rounded-lg'>
            <strong>
              There are {numSamplesForGenus.size.toString()} samples of {genusSpeciesName.toString()} across{' '}
              {numCountriesForGenus.size.toString()} countries
            </strong>
          </div>
        )}
        <div id='mapViewDivDetermination' className='h-96 w-full mt-4 bg-gray-200 rounded-md'></div>
      </div>
    </div>
  );
};

export default HsCode;
